import React from 'react';

import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { PMPluginFactoryParams, QuickInsertHandler } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';
import { isResolvingMentionProvider, type MentionProvider } from '@atlaskit/mention/resource';
import { isPromise } from '@atlaskit/mention/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { convertProsemirrorNodeToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { normaliseAIExperienceEvent } from '@atlassian/editor-ai-common/utils/normaliseEvents';

import type { AIPlugin } from './aiPluginType';
import {
	endAIExperience,
	endAISession,
	getPartialDocumentFromSelection,
	getPartialMarkdownFromSelection,
	openAIModal,
	startAISession,
	tagAsAIStreamingTransformation,
	updateTargetId,
} from './editor-actions/actions';
import type { AllowedAIEvents } from './editor-actions/types';
import { createAIAnnotationWatchmenPlugin } from './pm-plugins/ai-annotation-watchmen/ai-annotation-watchmen-plugin';
import { createAIAuditLogPlugin } from './pm-plugins/ai-audit-logs/ai-audit-log-plugin';
import { createAIButtonPlugin } from './pm-plugins/ai-button/ai-button-plugin';
import { aiButtonPluginKey } from './pm-plugins/ai-button/ai-button-plugin-key';
import { createAIEventHubPlugin } from './pm-plugins/ai-event-hub/ai-event-hub-plugin';
import { createAIMonitorWatchmenPlugin } from './pm-plugins/ai-monitor-watchmen/ai-monitor-watchmen-plugin';
import { createAIQuickstartPlugin } from './pm-plugins/ai-quickstart-command/ai-quickstart-command-plugin';
import {
	createAISessionContextPlugin,
	initialState as aiSessionInitialState,
} from './pm-plugins/ai-session-context/ai-session-context-plugin';
import { aiSessionContextPluginKey } from './pm-plugins/ai-session-context/ai-session-context-plugin-key';
import { dispatchAllowedStreamingEvent } from './pm-plugins/ai-session-context/commands';
import { createAISmartButtonAPIPlugin } from './pm-plugins/ai-smart-button-api/ai-smart-button-api-plugin';
import { createSpaceShortcutPlugin } from './pm-plugins/ai-space-shortcut/ai-space-shortcut-plugin';
import { createDecorationPlugin } from './pm-plugins/decoration/decoration-plugin';
import { aiExperienceDecorationPluginKey } from './pm-plugins/decoration/decoration-plugin-key';
import {
	createDocumentContentStatePlugin,
	documentContentStatePluginKey,
} from './pm-plugins/document-content-state/document-content-state-plugin';
import { createGetEditorViewPlugin } from './pm-plugins/get-editor-view/get-editor-view-plugin';
import { createInEditorDirectStreamingPlugin } from './pm-plugins/in-editor-direct-streaming/in-editor-direct-streaming-plugin';
import { inEditorDirectStreamingPluginKey } from './pm-plugins/in-editor-direct-streaming/in-editor-direct-streaming-plugin-key';
import { createInlineSuggestionPlugin } from './pm-plugins/inline-suggestions/inline-suggestion-plugin';
import { createPlaceholderControllerPlugin } from './pm-plugins/placeholder-controller/placeholder-controller-plugin';
import { createInEditorDirectStreamingPlugin as createPurePmInEditorDirectStreamingPlugin } from './pm-plugins/pure-pm-in-editor-direct-streaming/plugin';
import { purePmInEditorDirectStreamingPluginKey } from './pm-plugins/pure-pm-in-editor-direct-streaming/pluginKey';
import { rovoAgentsPluginKey } from './pm-plugins/rovo-agents/plugin-key';
import { createRovoAgentsPlugin } from './pm-plugins/rovo-agents/rovo-agents-plugin';
import { createSelectionPreviewPlugin } from './pm-plugins/selection-preview/selection-preview-plugin';
import { createSelectionPlugin } from './pm-plugins/selection/selection-plugin';
import { selectionPluginKey as aiExperienceSelectionPluginKey } from './pm-plugins/selection/selection-plugin-key';
import { type EditorPluginAIInitAEP } from './types/analytics';
import { ContentComponentWrapper } from './ui/components/ContentComponent/ContentComponentWrapper';
import { ContentComponentWrapper as ContentComponentWrapperLegacy } from './ui/components/ContentComponent/ContentComponentWrapperLegacy';
import { CONFIG_ITEM_KEYS } from './ui/prebuilt/config-items/config-item-keys';
import {
	getVisibleEditorPluginAIConfigItems,
	type EditorPluginAIConfigItem,
} from './ui/prebuilt/config-items/config-items';
import { mapConfigItemsToQuickInsertItems } from './ui/prebuilt/config-items/utils/map-config-items-to-quick-insert-items';
import { getFloatingToolbarConfig } from './ui/toolbars/floating-toolbar';
import { getAIPrimaryToolbarComponent } from './ui/toolbars/primary-toolbar';
import { getPrimaryToolbarLegacyComponent } from './ui/toolbars/primary-toolbar-legacy';
import { getSelectionToolbarConfig } from './ui/toolbars/selection-toolbar';
import { AIToolbarComponents } from './ui/toolbars/toolbar';
import { aiActorCache } from './ui/utils/xstate/ai-actor-cache';
import {
	aiExperienceActorStore,
	type AIExperienceMachineEvent,
} from './ui/utils/xstate/get-ai-experience-service';
// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required -- Ignored via go/ED-25883
/**
 * Creates an AI plugin instance for the editor.
 *
 * @deprecated
 *
 * `NextEditorPluginAI` is deprecated and will be removed in a future release.
 * Please use the `aiPlugin` export instead.
 *
 * This temporary export exists to facilitate a smooth transition to the new plugin naming convention.
 *
 * @param root0 - The plugin configuration object
 * @param root0.config - The AI plugin configuration
 * @param root0.api - The editor API
 * @returns The AI plugin instance
 *
 * @example
 * ```ts
 * // Old import
 * import { NextEditorPluginAI } from '@atlassian/editor-plugin-ai/EditorPluginAI';
 *
 * // New import
 * import { aiPlugin } from '@atlassian/editor-plugin-ai';
 * ```
 */
export const NextEditorPluginAI: AIPlugin = ({ config, api }) => {
	const editorPluginAIProvider = config;
	const aiGlobalOptIn = config.aiGlobalOptIn;
	let editorView: EditorView | undefined;
	const setEditorView = (newEditorView: EditorView) => {
		editorView = newEditorView;
	};
	const isToolbarAIFCEnabled = Boolean(api?.toolbar);

	// Determine wehther or not a preview modal will be shown, if it is then the user must manually accept/reject the changes.
	// if canInlineStreamChanges() => true, then the preview will not be shown and the changes will be applied automatically
	// If the canInlineStreamChanges() => false, then the changes will not be applied automatically, and the user will have to accept/reject the changes.
	const canInlineStreamChanges = () => {
		// The plugin can be passed a user prefs callback which could contain a setting which would disable in-document streaming.
		// We will default to true if this setting is not provided.
		const { isInDocumentStreamingAllowed = true } =
			editorPluginAIProvider?.getUserPreferences?.() ?? {};

		return (
			// We only allow auto-accepting AI changes if the in-document streaming feature flag is enabled
			!!editorPluginAIProvider.isInDocumentStreamingEnabled && isInDocumentStreamingAllowed
		);
	};

	let providerFactory: ProviderFactory;
	const setProviderFactory = (newProviderFactory: ProviderFactory) => {
		providerFactory = newProviderFactory;
	};

	let mentionProvider: MentionProvider | undefined;

	/**
	 * Helper function to retrieve the name details for a mention from the mentionProvider
	 * @param id - The mention ID to resolve
	 * @returns The mention name details or undefined if not found
	 * @example
	 * ```ts
	 * const mentionDetails = await getMentionNameDetails('user-123');
	 * ```
	 */
	const getMentionNameDetails = async (id: string) => {
		if (!mentionProvider || !isResolvingMentionProvider(mentionProvider)) {
			return;
		}

		const nameDetails = mentionProvider.resolveMentionName(id);

		if (isPromise(nameDetails)) {
			const mention = await nameDetails;
			return mention;
		}

		return nameDetails;
	};

	const setMentionProvider = () => {
		const handler = (_name: string, providerPromise?: Promise<MentionProvider>) => {
			if (providerPromise) {
				providerPromise.then((provider) => {
					mentionProvider = provider;
					providerFactory.unsubscribe('mentionProvider', handler);
				});
			}
		};
		providerFactory.subscribe('mentionProvider', handler);
	};

	const setMediaProvider = () => {
		if (editorPluginAIProvider?.providers?.mediaProvider) {
			providerFactory.setProvider(
				'mediaProvider',
				Promise.resolve(editorPluginAIProvider.providers.mediaProvider),
			);
		}
	};

	const setEmojiProvider = () => {
		const { emojiProvider } = api?.emoji?.sharedState.currentState() || {};
		if (!!emojiProvider) {
			providerFactory.setProvider('emojiProvider', Promise.resolve(emojiProvider));
		} else {
			const unsub = api?.emoji?.sharedState.onChange((sharedState) => {
				if (!!sharedState.nextSharedState?.emojiProvider) {
					providerFactory.setProvider(
						'emojiProvider',
						Promise.resolve(sharedState.nextSharedState.emojiProvider),
					);
					unsub?.();
				}
			});
		}
	};

	const setExtensionProvider = () => {
		const { extensionProvider } = api?.extension?.sharedState.currentState() || {};
		if (!!extensionProvider) {
			providerFactory.setProvider('extensionProvider', Promise.resolve(extensionProvider));
		} else {
			const unsub = api?.extension?.sharedState.onChange((sharedState) => {
				if (!!sharedState.nextSharedState?.extensionProvider) {
					providerFactory.setProvider(
						'extensionProvider',
						Promise.resolve(sharedState.nextSharedState.extensionProvider),
					);
					unsub?.();
				}
			});
		}
	};

	const aiOptIn = aiGlobalOptIn || {
		status: 'enabled',
		triggerOptInFlow: () => {},
	};

	const quickInsert: QuickInsertHandler = (intl) => {
		const configItems: EditorPluginAIConfigItem[] = [];

		if (!editorPluginAIProvider.disableQuickInsert) {
			if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
				configItems.push(editorPluginAIProvider.baseGenerate);
			} else {
				configItems.push(
					editorPluginAIProvider.baseGenerate,
					...getVisibleEditorPluginAIConfigItems({
						configItemWithOptions: editorPluginAIProvider.configItemWithOptions,
						// The editor view will always be available when quick insert items are being created
						// Ignored via go/ees005
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						editorView: editorView!,
						positions: [0, 0],
						isEmpty: true,
					}),
				);
			}
		}

		const quickInsertItems = mapConfigItemsToQuickInsertItems({
			configItems,
			formatMessage: intl.formatMessage,
			aiGlobalOptIn: aiOptIn,
			// The editor view will always be available when quick insert items are being created
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			editorView: editorView!,
			locale: intl.locale,
		});

		return quickInsertItems;
	};

	quickInsert.disableMemo = true;

	if (isToolbarAIFCEnabled) {
		const shouldShowImproveDescription =
			config.configItemWithOptions?.[0]?.config?.key === CONFIG_ITEM_KEYS.ADD_STRUCTURE;
		api?.toolbar?.actions.registerComponents(
			AIToolbarComponents({
				aiGlobalOptIn: aiOptIn,
				editorPluginAIProvider,
				shouldShowImproveDescription,
				getAdditionalProps: config.getAdditionalProps,
				isNewToolbarEnabled: true,
				api,
			}),
		);
	} else {
		if (!editorPluginAIProvider?.hidePrimaryToolbarButton) {
			api?.primaryToolbar?.actions.registerComponent({
				name: 'aiExperience',
				component: getAIPrimaryToolbarComponent({
					api,
					editorPluginAIProvider,
					aiGlobalOptIn: aiOptIn,
				}),
			});
		}
	}

	const adfStreamingEnabled = fg('platform_editor_ai_adf_prompts_in_all_products');

	return {
		name: 'aiExperience',
		pmPlugins: () => {
			const plugins = [
				{
					name: 'ai-content-state',
					plugin: () => createDocumentContentStatePlugin(),
				},
				{
					name: 'ai-experience-actor',
					plugin: () =>
						new SafePlugin({
							view: (editorView) => {
								// This sets up an [actor](https://xstate.js.org/docs/guides/actors.html#actor-api) using
								// the editor-plugin-ai machine and starts it up.
								const editorPluginAIActor = aiActorCache.getActor(editorView);
								return {
									destroy: () => {
										// This cleansup any listeners from the actor -- to ensure we don't leak memory.
										editorPluginAIActor?.stop();
										aiActorCache.delete(editorView);
									},
								};
							},
						}),
				},
				{
					name: 'aiExperience',
					plugin: (options: PMPluginFactoryParams) => {
						const payload: EditorPluginAIInitAEP = {
							action: 'init',
							actionSubject: 'editor',
							actionSubjectId: 'editorPluginAI',
							eventType: EVENT_TYPE.TRACK,
							attributes: {
								aiGlobalOptIn: aiOptIn?.status,
							},
						};
						// @ts-expect-error - This prevent type errors when trying to pass EditorPluginAI payload to AnalyticsEventPayload
						options.dispatchAnalyticsEvent(payload);

						return createDecorationPlugin({
							dispatchAnalyticsEvent: options.dispatchAnalyticsEvent,
							dispatch: options.dispatch,
							api,
							isInDocumentStreamingEnabled: editorPluginAIProvider.isInDocumentStreamingEnabled,
						});
					},
				},
				{
					name: 'ai-experience-get-editor-view-and-provider-factory',
					plugin: (options: PMPluginFactoryParams) => {
						setProviderFactory(options.providerFactory);
						setMentionProvider();
						setMediaProvider();
						setEmojiProvider();
						if (adfStreamingEnabled) {
							setExtensionProvider();
						}
						return createGetEditorViewPlugin({
							setEditorView: setEditorView,
						});
					},
				},
				{
					name: 'ai-experience-ai-button',
					plugin: () => createAIButtonPlugin(),
				},
				{
					name: 'ai-experience-selection-preview',
					plugin: () => createSelectionPreviewPlugin(),
				},
				{
					name: 'inline-suggestions',
					plugin: ({ dispatch, getIntl }: PMPluginFactoryParams) =>
						createInlineSuggestionPlugin({
							dispatch,
							getIntl,
							api,
						}),
				},
			];

			if (fg('platform_editor_ai_migrate_from_xstate')) {
				plugins.push({
					name: 'ai-session-context',
					plugin: ({ dispatch, getIntl }: PMPluginFactoryParams) =>
						createAISessionContextPlugin({
							api,
							dispatch,
							editorPluginAIProvider,
							getIntl,
							getMentionNameDetails,
						}),
				});
			}

			if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
				plugins.push({
					name: 'ai-experience-placeholder-controller',
					plugin: () => createPlaceholderControllerPlugin({ api, editorPluginAIProvider }),
				});
			}

			plugins.push({
				name: 'ai-event-hub',
				plugin: ({ getIntl }: PMPluginFactoryParams) =>
					createAIEventHubPlugin({
						aiGlobalOptIn: aiOptIn,
						editorPluginAIProvider,
						getIntl,
						api,
					}),
			});

			if (editorPluginAIProvider.isRovoEnabled) {
				plugins.push({
					name: 'editor-ai-rovo-agents',
					plugin: (options: PMPluginFactoryParams) => createRovoAgentsPlugin(options),
				});
				plugins.push({
					name: 'ai-experience-selection',
					plugin: () =>
						createSelectionPlugin({
							api,
							editorPluginAIProvider,
						}),
				});
			}

			if (
				editorPluginAIProvider.auditLogSettings &&
				editorPluginAIProvider.auditLogSettings.enabled &&
				fg('platform_editor_ai_audit_logs_events')
			) {
				const eventData = editorPluginAIProvider.auditLogSettings.eventData;
				plugins.push({
					name: 'ai-audit-log',
					plugin: ({ dispatch }: PMPluginFactoryParams) =>
						createAIAuditLogPlugin({
							dispatch,
							eventData,
						}),
				});
			}

			const { aiSmartButtonOptions, dynamicSurfaceConfigItems } = editorPluginAIProvider;
			if (aiSmartButtonOptions && dynamicSurfaceConfigItems && fg('ai-smart-button-team-25')) {
				plugins.push({
					name: 'ai-smart-button-api',
					plugin: ({ getIntl, dispatch }: PMPluginFactoryParams) =>
						createAISmartButtonAPIPlugin({
							aiGlobalOptIn: aiOptIn,
							aiSmartButtonOptions,
							api,
							dispatch,
							dynamicSurfaceConfigItems,
							getIntl,
							product: editorPluginAIProvider.product,
							getMentionNameDetails,
						}),
				});
			}

			if (editorExperiment('platform_editor_ai_quickstart_command', true, { exposure: true })) {
				plugins.push({
					name: 'ai-quickstart-command',
					plugin: ({ getIntl }: PMPluginFactoryParams) =>
						createAIQuickstartPlugin({
							aiGlobalOptIn: aiOptIn,
							editorPluginAIProvider,
							getIntl,
						}),
				});
			}

			if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
				const canAutoAcceptAIChanges = canInlineStreamChanges();
				if (fg('platform_editor_ai_migrate_from_xstate')) {
					plugins.push({
						name: 'pure-pm-in-editor-direct-streaming',
						plugin: ({ getIntl }: PMPluginFactoryParams) =>
							createPurePmInEditorDirectStreamingPlugin({
								getIntl,
								api,
								canAutoAcceptAIChanges,
							}),
					});
				} else {
					plugins.push({
						name: 'in-editor-direct-streaming',
						plugin: ({ getIntl }: PMPluginFactoryParams) =>
							createInEditorDirectStreamingPlugin({
								getIntl,
								api,
							}),
					});
				}

				plugins.push({
					name: 'ai-annotation-watchmen',
					plugin: () =>
						createAIAnnotationWatchmenPlugin({
							onAnnotationsRemoved: editorPluginAIProvider.onAnnotationsRemoved,
						}),
				});

				plugins.push({
					name: 'ai-monitor-watchmen',
					plugin: () => createAIMonitorWatchmenPlugin(api, canInlineStreamChanges),
				});

				if (
					editorPluginAIProvider.product === 'CONFLUENCE' &&
					fg('platform_editor_ai_aifc_space_shortcut')
				) {
					plugins.push({
						name: 'ai-space-shortcut',
						plugin: ({ getIntl }) =>
							createSpaceShortcutPlugin({
								aiGlobalOptIn: aiOptIn,
								editorPluginAIProvider,
								getIntl,
							}),
					});
				}
			}

			return plugins;
		},

		actions: {
			openAIModal: ({
				invokedFrom,
				configItem = editorPluginAIProvider.baseGenerate,
				withAcceptReject,
				withStreamingHighlight,
				hideLoadingScreen,
				traceId,
				singleInstrumentationId,
				isCreate,
			}) => {
				openAIModal({
					invokedFrom,
					editorView,
					aiOptIn,
					configItem,
					withAcceptReject,
					withStreamingHighlight,
					isExternal: true,
					hideLoadingScreen,
					traceId,
					singleInstrumentationId,
					isCreate,
				});
			},
			endAIExperience: () => {
				endAIExperience({ editorView });
			},
			getAiExperienceActor: () => {
				if (!editorView) {
					throw new Error('editorView is not defined');
				}

				const actor = aiExperienceActorStore.getActor(editorView);
				if (!actor) {
					throw new Error('No cached item found');
				}

				return actor;
			},
			getAISelectionContext: () => {
				const selection = api?.selection?.sharedState.currentState()?.selection;
				const selectionContent = selection?.content().content;
				return (
					api?.aiExperience?.sharedState.currentState()?.selectionContext ?? {
						selectionFragment: api?.selection?.actions.getSelectionFragment() ?? null,
						localIds: api?.selection?.actions.getSelectionLocalIds() ?? null,
						selectionMarkdown: selectionContent
							? convertProsemirrorNodeToMarkdown({ node: selectionContent })?.markdown
							: null,
						startIndex: fg('platform_editor_ai_extend_selection_context')
							? (selection?.$from.parentOffset ?? null)
							: undefined,
						endIndex: fg('platform_editor_ai_extend_selection_context')
							? (selection?.$to.parentOffset ?? null)
							: undefined,
					}
				);
			},
			getPartialDocumentFromSelection: (maxSize) => {
				return getPartialDocumentFromSelection({
					selection: editorView?.state.selection,
					maxSize,
				});
			},
			getPartialMarkdownFromSelection: (maxSize) => {
				return getPartialMarkdownFromSelection({
					selection: editorView?.state.selection,
					maxSize,
				});
			},
			startSession: (sessionId: string, traceId?: string) => {
				api?.core?.actions.execute(api?.history?.commands?.startHistorySlice('ai'));
				startAISession({
					editorView,
					sessionId,
					traceId: fg('platform_editor_ai_traceid_in_analytics_events') ? traceId : undefined,
				});
			},
			endSession: (sessionId: string, metadata: { type: 'abort' | 'finished' | 'error' }) => {
				endAISession({ editorView, sessionId, metadata });
				api?.core?.actions.execute(api?.history?.commands?.endHistorySlice('ai'));
			},
			updateTargetId: (targetId: string | number) => {
				updateTargetId({ editorView, targetId });
			},
			dispatchAIEvent: (event: AllowedAIEvents) => {
				if (fg('platform_editor_ai_migrate_from_xstate')) {
					api?.core.actions.execute(dispatchAllowedStreamingEvent(event));
				} else {
					const normalisedEvent = normaliseAIExperienceEvent(event);
					api?.aiExperience?.actions
						.getAiExperienceActor()
						?.send(normalisedEvent as AIExperienceMachineEvent);
				}
			},
			tagAsAIStreamingTransformation,
			fireUnifiedAnalyticsEvent: (payload: Parameters<typeof createUnifiedAnalyticsPayload>[0]) => {
				api?.analytics?.actions.fireAnalyticsEvent(createUnifiedAnalyticsPayload(payload));
			},
		},
		getSharedState(state: EditorState | undefined) {
			if (!state) {
				return {
					nonCursorSelectionChangeCount: 0,
					aiButtonState: {
						triggeredFor: undefined,
						isSelectionValidForAIExperience: true,
					},
					aiDecorationExperiencePluginState: {
						isRunning: false,
						interactionSessionId: undefined,
						modalDecorationSet: DecorationSet.empty,
						configItem: undefined,
						lastTriggeredFrom: undefined,
						modalMountedTimeStamp: undefined,
						triggerMethod: undefined,
						initialPrompt: undefined,
						triggeredFor: undefined,
						initialPositions: undefined,
						nudgeMetrics: undefined,
						hideLoadingScreen: false,
						targetId: undefined,
						isInDocumentStreamingEnabled: editorPluginAIProvider.isInDocumentStreamingEnabled,
						traceId: undefined,
					},
					rovoAgentsPluginState: {
						positions: undefined,
						isBrowseModalOpen: false,
					},
					isStreaming: false,
					isShowingAcceptRejectScreen: false,
					chunkNumber: 0,
					selectionContext: undefined,
					hasVisibleContent: false,
					aiSessionPluginState: aiSessionInitialState,
				};
			}

			return {
				nonCursorSelectionChangeCount: (aiExperienceSelectionPluginKey.getState(state) ?? {})
					?.nonCursorSelectionChangeCount,
				aiButtonState: {
					triggeredFor: (aiButtonPluginKey.getState(state) ?? {}).triggeredFor,
					isSelectionValidForAIExperience: (aiButtonPluginKey.getState(state) ?? {})
						.isSelectionValidForAIExperience,
				},
				aiDecorationExperiencePluginState: {
					isRunning: (aiExperienceDecorationPluginKey.getState(state) ?? {}).isRunning ?? false,
					modalDecorationSet:
						(aiExperienceDecorationPluginKey.getState(state) ?? {}).modalDecorationSet ??
						DecorationSet.empty,
					configItem: (aiExperienceDecorationPluginKey.getState(state) ?? {}).configItem,
					lastTriggeredFrom: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.lastTriggeredFrom,
					modalMountedTimeStamp: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.modalMountedTimeStamp,
					triggerMethod: (aiExperienceDecorationPluginKey.getState(state) ?? {}).triggerMethod,
					initialPrompt: (aiExperienceDecorationPluginKey.getState(state) ?? {}).initialPrompt,
					triggeredFor: (aiExperienceDecorationPluginKey.getState(state) ?? {}).triggeredFor,
					initialPositions: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.initialPositions,
					positions: (aiExperienceDecorationPluginKey.getState(state) ?? {}).positions,
					nudgeMetrics: (aiExperienceDecorationPluginKey.getState(state) ?? {}).nudgeMetrics,
					currentSessionId: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.currentSessionId,
					nextSessionId: (aiExperienceDecorationPluginKey.getState(state) ?? {}).nextSessionId,
					interactionSessionId: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.interactionSessionId,
					hideLoadingScreen: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.hideLoadingScreen,
					targetId: (aiExperienceDecorationPluginKey.getState(state) ?? {}).targetId,
					isInDocumentStreamingEnabled: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.isInDocumentStreamingEnabled,
					traceId: fg('platform_editor_ai_traceid_in_analytics_events')
						? (aiExperienceDecorationPluginKey.getState(state) ?? {}).traceId
						: undefined,
				},
				rovoAgentsPluginState: {
					positions: (rovoAgentsPluginKey.getState(state) ?? {}).positions,
					isBrowseModalOpen: (rovoAgentsPluginKey.getState(state) ?? {}).isBrowseModalOpen ?? false,
					defaultAgent: (rovoAgentsPluginKey.getState(state) ?? {}).defaultAgent,
				},
				isStreaming: fg('platform_editor_ai_migrate_from_xstate')
					? (purePmInEditorDirectStreamingPluginKey.getState(state)?.isStreaming ?? false)
					: (inEditorDirectStreamingPluginKey.getState(state)?.isStreaming ?? false),
				isShowingAcceptRejectScreen:
					(aiExperienceDecorationPluginKey.getState(state) ?? {}).isShowingAcceptRejectScreen ??
					false,
				chunkNumber: fg('platform_editor_ai_migrate_from_xstate')
					? (purePmInEditorDirectStreamingPluginKey.getState(state)?.chunkNumber ?? 0)
					: (inEditorDirectStreamingPluginKey.getState(state)?.chunkNumber ?? 0),
				// Streaming plugin state for React hooks (scroll, mount effects, etc.)
				streamingState: fg('platform_editor_ai_migrate_from_xstate')
					? {
							streamStartPos:
								purePmInEditorDirectStreamingPluginKey.getState(state)?.streamStartPos,
							scrollTargetPos:
								purePmInEditorDirectStreamingPluginKey.getState(state)?.scrollTargetPos,
							streamPhase: purePmInEditorDirectStreamingPluginKey.getState(state)?.streamPhase,
							currentSessionId:
								purePmInEditorDirectStreamingPluginKey.getState(state)?.currentSessionId,
						}
					: undefined,
				selectionContext:
					aiExperienceSelectionPluginKey.getState(state)?.lastSelectionContext ?? undefined,
				hasVisibleContent:
					documentContentStatePluginKey.getState(state)?.hasVisibleContent ?? false,
				aiSessionPluginState: aiSessionContextPluginKey.getState(state) ?? aiSessionInitialState,
			};
		},
		pluginsOptions: {
			...(!isToolbarAIFCEnabled && {
				selectionToolbar: getSelectionToolbarConfig({
					aiGlobalOptIn: aiOptIn,
					editorPluginAIProvider,
					api,
				}),
			}),
			quickInsert: quickInsert,
			floatingToolbar: getFloatingToolbarConfig({
				aiGlobalOptIn: aiOptIn,
				editorPluginAIProvider,
				api,
			}),
		},

		contentComponent: ({
			editorView,
			wrapperElement,
			appearance,
			providerFactory,
			popupsMountPoint,
		}) => {
			if (!editorView) {
				return null;
			}

			// We're going to use a different collection of components for the AIFC experience. This should allow for
			// a better SoC and easier cleanup in the future and it will provide clear separation between the two, old/new experiences.
			// This also means we do not need to reference the AIFC feature flag in multiple places throughout the UI.
			const C = editorPluginAIProvider.isInDocumentStreamingEnabled
				? ContentComponentWrapper
				: ContentComponentWrapperLegacy;

			return (
				<C
					aiOptIn={aiOptIn}
					api={api}
					editorView={editorView}
					wrapperElement={wrapperElement}
					appearance={appearance}
					editorPluginAIProvider={editorPluginAIProvider}
					providerFactory={providerFactory}
					getMentionNameDetails={getMentionNameDetails}
					canInlineStreamChanges={canInlineStreamChanges}
					isInDocumentStreamingEnabled={!!editorPluginAIProvider.isInDocumentStreamingEnabled}
					popupsMountPoint={popupsMountPoint}
					endExperience={() => {}}
				/>
			);
		},

		primaryToolbarComponent:
			!api?.primaryToolbar && !editorPluginAIProvider.hidePrimaryToolbarButton
				? getPrimaryToolbarLegacyComponent({
						api,
						editorPluginAIProvider,
						aiGlobalOptIn: aiOptIn,
					})
				: undefined,
	};
};

// Ignored via go/ees007
// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
/**
 * TODO: Remove this when making a major change as this was originally exported here
 * (Probably when deprecating the legacy plugin)
 * This type is currently used in some products.
 */
// eslint-disable-next-line @atlaskit/editor/no-re-export
export type { AIPlugin } from './aiPluginType';
